// ---------------------------------------------------------------------------
// GraderProvider — owns the Judge Factory page's persistent state:
// the judge collection (via useGraders), navigation, dialog state, and the
// collection-mutation actions. Also owns the Toaster instance so mutation
// hooks can dispatch toasts via the actions context without prop-drilling.
//
// In create mode the page renders the system-prompt create flow
// ([[SystemPromptCreateLayout]]), which manages its own local form state.
//
// Splits into two contexts to keep re-renders scoped:
//   * GraderStateContext — nav slice + judges collection.
//   * GraderActionsContext — stable action callbacks + dispatchToast;
//     consumers that only dispatch never re-render on state changes.
// ---------------------------------------------------------------------------

import { Toaster, useId, useToastController } from '@fluentui/react-components';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import type { PropsWithChildren } from 'react';

import type { Grader } from '../hooks/useGraders/useGraders.interface.js';
import { useGraders } from '../hooks/useGraders/useGraders.js';

import type { GraderState } from './grader.interface.js';
import { INITIAL_JUDGE_FACTORY_NAV_STATE } from './grader.interface.js';
import { graderReducer } from './grader.reducer.js';
import type { GraderActions } from './GraderContext.interface.js';

// Re-export the public types for consumers that import them from this
// implementation file.
export type {
  DispatchToast,
  GraderActions,
} from './GraderContext.interface.js';
export type { GraderState } from './grader.interface.js';
export type { Grader };

const GraderStateContext = createContext<GraderState | null>(null);
const GraderActionsContext = createContext<GraderActions | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface GraderProviderProps {
  /** Workspace whose graders to load. Passed to the internal useGraders. */
  workspaceName: string;
  /** Optional initial judge ID from URL deep link. */
  initialJudgeId?: string | null;
}

export function GraderProvider({
  workspaceName,
  initialJudgeId,
  children,
}: PropsWithChildren<GraderProviderProps>) {
  const toasterId = useId('graders-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const {
    graders,
    setGraders,
    loading: gradersLoading,
    error: gradersError,
  } = useGraders({ workspaceName });

  const [navState, dispatch] = useReducer(graderReducer, {
    ...INITIAL_JUDGE_FACTORY_NAV_STATE,
    selectedId: initialJudgeId ?? null,
  });

  // Self-correcting invariant: when the user is in view mode, the selection
  // must point at a judge present in the list.
  // Skips while judges are loading to avoid clearing a URL-based initial
  // selection before the list has arrived.
  useEffect(() => {
    if (gradersLoading) return;
    if (navState.mode !== 'view') return;
    if (graders.length === 0) {
      if (navState.selectedId !== null) {
        dispatch({ type: 'SELECT_JUDGE', id: null });
      }
      return;
    }
    if (
      navState.selectedId === null ||
      !graders.some((j) => j.id === navState.selectedId)
    ) {
      // `graders` is already sorted newest-first (see useGraders), so the
      // first entry is the top row of the list.
      dispatch({ type: 'SELECT_JUDGE', id: graders[0]?.id ?? null });
    }
  }, [graders, gradersLoading, navState.mode, navState.selectedId]);

  const selectedGrader = useMemo<Grader | null>(
    () =>
      navState.selectedId === null
        ? null
        : (graders.find((j) => j.id === navState.selectedId) ?? null),
    [graders, navState.selectedId],
  );

  const state = useMemo<GraderState>(
    () => ({
      ...navState,
      workspaceName,
      graders,
      selectedGrader,
      gradersLoading,
      gradersError,
    }),
    [
      navState,
      workspaceName,
      graders,
      selectedGrader,
      gradersLoading,
      gradersError,
    ],
  );

  const insertGrader = useCallback(
    (judge: Grader) => setGraders((prev) => [judge, ...prev]),
    [setGraders],
  );

  const patchGrader = useCallback(
    (id: string, patch: Partial<Grader>) =>
      setGraders((prev) =>
        prev.map((j) => (j.id === id ? { ...j, ...patch } : j)),
      ),
    [setGraders],
  );

  const removeGrader = useCallback(
    (id: string) => setGraders((prev) => prev.filter((j) => j.id !== id)),
    [setGraders],
  );

  const actions = useMemo<GraderActions>(
    () => ({
      // Navigation
      selectGrader: (id) => dispatch({ type: 'SELECT_JUDGE', id }),
      setEditorTab: (tab) => dispatch({ type: 'SET_EDITOR_TAB', tab }),
      enterCreateMode: (method) =>
        dispatch({ type: 'ENTER_CREATE_MODE', method }),
      exitCreateMode: (selectedId) =>
        dispatch({ type: 'EXIT_CREATE_MODE', selectedId }),
      // Dialogs
      openDeleteDialog: (judge) =>
        dispatch({ type: 'OPEN_DELETE_DIALOG', judge }),
      closeDeleteDialog: () => dispatch({ type: 'CLOSE_DELETE_DIALOG' }),
      // Judge collection
      insertGrader,
      patchGrader,
      removeGrader,
      // Toast
      dispatchToast,
    }),
    [insertGrader, patchGrader, removeGrader, dispatchToast],
  );

  return (
    <GraderStateContext.Provider value={state}>
      <GraderActionsContext.Provider value={actions}>
        <Toaster toasterId={toasterId} position="bottom-end" />
        {children}
      </GraderActionsContext.Provider>
    </GraderStateContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumer hooks
// ---------------------------------------------------------------------------

export function useGraderState(): GraderState {
  const ctx = useContext(GraderStateContext);
  if (ctx === null) {
    throw new Error('useGraderState must be used within <GraderProvider>');
  }
  return ctx;
}

export function useGraderActions(): GraderActions {
  const ctx = useContext(GraderActionsContext);
  if (ctx === null) {
    throw new Error('useGraderActions must be used within <GraderProvider>');
  }
  return ctx;
}
